import { useEffect, useState } from "react";
import { useDataReload } from "hooks/useDataReload";

export const useDataGrid = ({
  url,
  reloadIntervalInSeconds = 0,
  defaultSorter = {
    name: "id",
    order: "desc",
  },
  defaultLimit = 25,
  additionalParams = {},
  gridConfigIndex,
  setLoading,
}) => {
  const [error, setError] = useState("");
  const defaultVisibility = JSON.parse(
    window.localStorage.getItem(gridConfigIndex) ?? "[]"
  );

  const [search, setSearch] = useState("");
  const [sorter, setSorter] = useState(defaultSorter);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState();
  const [visibility, setVisibilityState] = useState(defaultVisibility);

  /** @type {React.Dispatch<any>} */
  const setVisibility = (value) => {
    setVisibilityState(value);
    window.localStorage.setItem(gridConfigIndex, JSON.stringify(value));
  };

  const getApiRequestParams = () => ({
    search,
    sorter,
    from: limit * (currentPage - 1),
    limit,
    ...additionalParams,
  });

  const loadData = async () => {
    setError("");
    setLoading(true);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(getApiRequestParams()),
      });
      const result = await response.json();

      if (result?.error) {
        setError(result?.error);
      }

      setRows(result?.records ?? []);
      setTotalCount(result?.count ?? 0);
    } catch (err) {
      setRows([]);
      setTotalCount(0);
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (!field) return;

    if (sorter.name === field) {
      setSorter({
        name: field,
        order: sorter.order === "asc" ? "desc" : "asc",
      });
      return;
    }

    setSorter({
      name: field,
      order: "asc",
    });

    setCurrentPage(1);
  };

  const handleChangeSearch = (e) => {
    const value = e?.target?.value;

    setSearch(value);
    setCurrentPage(1);
  };

  const clearSearch = () => setSearch("");

  const totalPages = Math.ceil(totalCount / limit);

  const handleChangeRecordsPerPage = (e) => {
    const value = e?.target?.value;
    setLimit(value);
    setCurrentPage(1);
  };

  const handleChangePage = (event, value) => {
    setCurrentPage(value);
  };

  useDataReload({
    loadData,
    skipFirstLoad: true,
    reloadIntervalInSeconds,
  });

  useEffect(() => {
    if (rows) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorter, currentPage, limit, additionalParams]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadData();
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return {
    search,
    setSearch,
    handleChangeSearch,
    clearSearch,
    sorter,
    setSorter,
    handleSort,
    currentPage,
    totalPages,
    handleChangePage,
    handleChangeRecordsPerPage,
    setCurrentPage,
    limit,
    setLimit,
    getApiRequestParams,
    rows,
    loadData,
    totalCount,
    visibility,
    setVisibility,
    defaultLimit,
    error,
    setError,
  };
};
